import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { NoticeCompanyUserStatus } from '../../../../../../api/theHive/notices/enumerations';
import {
  ChangeRequestData,
  NoticeCompanyTeamUser,
} from '../../../../../../api/theHive/notices/types';
import { ColoredCircularProgress } from '../../../../../../components/UI/Typography';
import { Constants } from '../../../../../../constants/hiveNotices';
import { IconExpandMoreMS } from '../../../../../../constants/icons';
import { formatDate, formatOnlyDate } from '../../../../../../helpers';
import {
  StyledTooltip,
  TeamMemberAccordionBox,
  TeamMemberDateCaption,
  TeamMemberDetails,
  TeamMemberInfo,
  TeamMemberLoadingBox,
  TeamMemberMeta,
  TeamMemberName,
  TeamMemberStatusBadge,
  TeamMemberStatusBox,
  TeamMemberStatusDot,
  TeamMemberSummary,
  TeamHintText,
} from '../styles';
import useCriteriaDocuments from '../useCriteriaDocuments';
import useMemberChangeRequests from '../useMemberChangeRequests';
import {
  getMemberChangeRequestOwnerIds,
  getMemberScopeId,
  getRemovalTerm,
  getPendingChangeRequestDocIds,
  getTeamMemberDisplayStatus,
  getTeamMemberTermHint,
  getTeamMemberStatusStyle,
  hasPendingMemberRequest,
  isEngineeringFrozenByAdditionTerm,
  summarizeTeamMemberTags,
} from '../utils';
import { MemberEngineeringDocuments } from './MemberEngineeringDocuments';

interface TeamMemberAccordionProps {
  noticeCompanyId: number;
  user: NoticeCompanyTeamUser;
  expanded: boolean;
  onToggle: (memberId: number, isExpanded: boolean) => void;
  pendingChangeRequests: ChangeRequestData[];
  actionsDisabled: boolean;
  docLoading: boolean;
  handleReplace: (docId: number) => Promise<void>;
  handleApprove: (docId?: number) => Promise<void>;
  handleApproveWithReservation: (
    reservationNote: string,
    docId: number
  ) => Promise<void>;
  handleReject: (reason: string, docId?: number) => Promise<void>;
}

const getRegisterLabel = (user: NoticeCompanyTeamUser): string | undefined => {
  if (!user.professional_document) return undefined;
  const type = user.professional_document_type?.toUpperCase();
  return type
    ? `${type} ${user.professional_document}`
    : user.professional_document;
};

const getStatusDate = (user: NoticeCompanyTeamUser): string | undefined => {
  if (user.status === NoticeCompanyUserStatus.LEAVING) {
    const removalRequestedAt = getRemovalTerm(user)?.requested_at;
    if (removalRequestedAt) {
      return `${Constants.teamRemovalRequestedAtLabel} ${formatOnlyDate(
        removalRequestedAt
      )}`;
    }
  }

  if (
    hasPendingMemberRequest(user) &&
    user.status !== NoticeCompanyUserStatus.PENDING
  ) {
    return Constants.teamPendingRequestLabel;
  }

  if (user.status === NoticeCompanyUserStatus.PAUSED && user.absence) {
    return Constants.teamAbsenceRangeLabel
      .replace('{start}', formatDate(user.absence.start_date))
      .replace('{end}', formatDate(user.absence.end_date));
  }

  if (user.status === NoticeCompanyUserStatus.APPROVED && user.approved_at) {
    return `${Constants.teamApprovedAtLabel} ${formatOnlyDate(
      user.approved_at
    )}`;
  }

  if (user.requested_at) {
    return `${Constants.teamRequestedAtLabel} ${formatOnlyDate(
      user.requested_at
    )}`;
  }

  return undefined;
};

export function TeamMemberAccordion({
  noticeCompanyId,
  user,
  expanded,
  onToggle,
  pendingChangeRequests,
  actionsDisabled,
  docLoading,
  handleReplace,
  handleApprove,
  handleApproveWithReservation,
  handleReject,
}: TeamMemberAccordionProps): JSX.Element {
  const memberScopeId = getMemberScopeId(user);

  const { documents, loading, fetchDocuments } = useCriteriaDocuments(
    noticeCompanyId,
    memberScopeId
  );

  const { changeRequests, fetchChangeRequests } = useMemberChangeRequests(
    noticeCompanyId,
    user.id
  );

  const handleExpand = useCallback(
    (_: React.SyntheticEvent, isExpanded: boolean): void => {
      onToggle(user.id, isExpanded);
    },
    [onToggle, user.id]
  );

  const requestAttempted = useRef(false);

  const [fetchStarted, setFetchStarted] = useState(false);

  useEffect(() => {
    if (!expanded) {
      if (!documents) {
        requestAttempted.current = false;
        setFetchStarted(false);
      }
      return;
    }
    if (requestAttempted.current) return;
    requestAttempted.current = true;
    setFetchStarted(true);
    fetchDocuments();
    fetchChangeRequests();
  }, [expanded, documents, fetchDocuments, fetchChangeRequests]);

  // Every action goes through the page handler (snackbar + company refresh) and
  // is followed by a reload of this member's documents, so the statuses in the
  // open accordion reflect what was just decided.
  const refreshAfter = useCallback(
    (action: () => Promise<void>): Promise<void> =>
      action()
        .then(() => fetchDocuments())
        .then(() => fetchChangeRequests()),
    [fetchDocuments, fetchChangeRequests]
  );

  const engineeringFrozen = isEngineeringFrozenByAdditionTerm(user);

  // The accreditation request is only open for a verdict while it is under
  // analysis. Once approved or paused the documents are read-only: no
  // approving, approving with reservation, rejecting or disapproving. The
  // company-wide block (discredited) overrides the person's status: the link
  // that sustained the individual request no longer exists.
  const memberActionsDisabled =
    actionsDisabled ||
    user.status !== NoticeCompanyUserStatus.PENDING ||
    engineeringFrozen;

  // Exception to the rule above: an approved member who asked for a new pole
  // or criteria group is back under analysis for THAT request only. The
  // documents it brought stay actionable; everything else they already had
  // approved does not. The company-wide block still wins.
  const changeRequestDocIds = useMemo(
    () =>
      actionsDisabled || engineeringFrozen
        ? new Set<number>()
        : getPendingChangeRequestDocIds(
            [...pendingChangeRequests, ...changeRequests],
            getMemberChangeRequestOwnerIds(user)
          ),
    [
      actionsDisabled,
      engineeringFrozen,
      pendingChangeRequests,
      changeRequests,
      user,
    ]
  );

  const statusStyle = getTeamMemberStatusStyle(
    getTeamMemberDisplayStatus(user)
  );
  const register = getRegisterLabel(user);
  const groups = summarizeTeamMemberTags(
    user.criteria_groups,
    Constants.teamGroupSingular,
    Constants.teamGroupPlural
  );
  const poles = summarizeTeamMemberTags(
    user.coverages,
    Constants.teamPoleSingular,
    Constants.teamPolePlural
  );
  const contentLoading = !fetchStarted || loading;

  const statusDate = getStatusDate(user);
  const termHint = getTeamMemberTermHint(user);

  const buildMeta = (
    groupsPart: string | undefined,
    polesPart: string | undefined
  ): string[] =>
    [
      user.is_responsible ? Constants.teamCompanyResponsibleLabel : undefined,
      Constants.teamTechnicalResponsibleLabel,
      register,
      groupsPart,
      polesPart,
    ].filter((part): part is string => Boolean(part));

  // The line is truncated with an ellipsis, so the tooltip repeats it whole,
  // with the full group and pole lists in place of their "+N" suffixes.
  const metaParts = buildMeta(groups?.text, poles?.text);
  const metaTooltip = buildMeta(groups?.tooltip, poles?.tooltip).join(' · ');

  return (
    <TeamMemberAccordionBox
      expanded={expanded}
      onChange={handleExpand}
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <TeamMemberSummary expandIcon={IconExpandMoreMS}>
        <TeamMemberInfo>
          <TeamMemberName>{user.name}</TeamMemberName>
          <StyledTooltip title={metaTooltip} placement="top-start">
            <TeamMemberMeta>{metaParts.join(' · ')}</TeamMemberMeta>
          </StyledTooltip>
        </TeamMemberInfo>
        <TeamMemberStatusBox>
          {statusStyle && (
            <StyledTooltip title={termHint ?? ''} placement="top-end">
              <TeamMemberStatusBadge
                sx={{
                  color: statusStyle.color,
                  backgroundColor: statusStyle.background,
                }}
              >
                <TeamMemberStatusDot />
                <span>{statusStyle.label}</span>
              </TeamMemberStatusBadge>
            </StyledTooltip>
          )}
          {statusDate && (
            <TeamMemberDateCaption>{statusDate}</TeamMemberDateCaption>
          )}
        </TeamMemberStatusBox>
      </TeamMemberSummary>
      <TeamMemberDetails>
        {contentLoading && (
          <TeamMemberLoadingBox>
            <ColoredCircularProgress size={28} />
          </TeamMemberLoadingBox>
        )}
        {!contentLoading && !documents && (
          <TeamHintText>{Constants.memberDocumentsLoadError}</TeamHintText>
        )}
        {!contentLoading && documents && (
          <MemberEngineeringDocuments
            data={documents}
            docLoading={docLoading}
            handleReplace={(docId) => refreshAfter(() => handleReplace(docId))}
            handleApprove={(docId) => refreshAfter(() => handleApprove(docId))}
            handleApproveWithReservation={(reservationNote, docId) =>
              refreshAfter(() =>
                handleApproveWithReservation(reservationNote, docId)
              )
            }
            handleReject={(reason, docId) =>
              refreshAfter(() => handleReject(reason, docId))
            }
            disabled={memberActionsDisabled}
            changeRequestDocIds={changeRequestDocIds}
          />
        )}
      </TeamMemberDetails>
    </TeamMemberAccordionBox>
  );
}
